import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Stack, Typography, DataGrid, FilterSelect, IdentityCell, Link } from '../../ui/core';
import {
  CarIcon,
  CheckCircleIcon,
  DriveIcon,
  EventIcon,
  MailIcon,
  PersonIcon,
  PhoneIcon,
  RouteIcon,
  StationIcon,
} from '../../ui/icons';
import { cardHeaderSx } from '../../ui/styles';
import DriverDaysAgoChip from '../../components/widgets/DriversApproval/DriverDaysAgoChip';
import { fetchDrivers } from '../../redux/Drivers/actions';
import { useQuery, useFormatters, useCities } from '../../hooks';
import { useHasPermission } from '../../helpers/api/auth/index';
import { PERMISSIONS } from '../../helpers/api/auth/permissions';

// Fixed page size rather than one derived from window.innerHeight — decouples
// the row-fetch size from how much chrome sits above the grid.
const PAGE_SIZE = 25;
// Same fields as before — headerIcon + the name column's IdentityCell (avatar +
// name) are purely aesthetic, matching the clean table look.
const columns = [
  { field: 'id', hide: true },
  {
    field: 'name',
    headerName: 'First name, Last name',
    headerIcon: <PersonIcon />,
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => <IdentityCell name={row.name} colorKey={row.id} />,
  },
  { field: 'email', headerName: 'Email', headerIcon: <MailIcon />, flex: 1, minWidth: 160 },
  { field: 'phoneNumber', headerName: 'Phone Number', headerIcon: <PhoneIcon />, minWidth: 240 },
  { field: 'homeCity', headerName: 'Home City', headerIcon: <RouteIcon />, flex: 1, minWidth: 160 },
  { field: 'type', headerName: 'Type', headerIcon: <CarIcon />, minWidth: 240 },
  {
    field: 'daysAgo',
    headerName: 'Created (days)',
    headerIcon: <EventIcon />,
    minWidth: 160,
    renderCell: ({ row }) => <DriverDaysAgoChip driver={row} />,
  },
  { field: 'driver_id', headerName: 'Driver ID', headerIcon: <DriveIcon />, minWidth: 300 },
];

/**
 * A second entry point into the driver profile, not a replacement: the row click still
 * opens the funnel. stopPropagation is what keeps both reachable from one row.
 *
 * Appended only for those who can open it. This grid is gated on
 * `change_driver_status`, but /driver-profile needs `manage_drivers` — so without the
 * check a funnel-only user sees the link and is silently redirected to /home. A visible
 * link to a page you cannot open is the "hidden link is not access control" argument in
 * reverse.
 */
const PROFILE_COLUMN = {
  field: 'profile',
  headerName: 'Documents',
  headerIcon: <CheckCircleIcon />,
  minWidth: 130,
  sortable: false,
  renderCell: ({ row }) => (
    <Link
      to={`/driver-profile/${row.driver_id}`}
      onClick={(e) => e.stopPropagation()}
      style={{ fontSize: '13px' }}
    >
      Open profile
    </Link>
  ),
};

const DriversTriage = () => {
  const history = useHistory();
  const { driverApplicationStatus } = useParams();
  let { homeCity, page } = useQuery();
  page = Math.max(+page || 1, 1);
  const f = useFormatters();
  const hasChangeDriverStatusPermission = useHasPermission(PERMISSIONS.CHANGE_DRIVER_STATUS)
  const canManageDrivers = useHasPermission(PERMISSIONS.MANAGE_DRIVERS);
  const gridColumns = canManageDrivers ? [...columns, PROFILE_COLUMN] : columns;

  const { cities } = useCities();
  const { data, count, loading } = useSelector((state) => state.drivers.all);

  const cityLabelById = (id) => {
    const city = cities.find((c) => c.id === id);
    return city ? [city.cityName, city.stateAbbr].filter(Boolean).join(', ') : '';
  };

  const rows = data.map((d) => ({
    ...d,
    id: d.id,
    name: `${d.firstName} ${d.lastName}`,
    phoneNumber: f.phone.toInternational(d.phoneNumber),
    homeCity: cityLabelById(d.homeCity),
    type:
      (d.isCustomDriver ? 'On-call driver' : 'Going anyway driver') +
      (driverApplicationStatus === 'resubmitted_docs' && d.isDriverVerified ? ' ✓' : ''),
    daysAgo: d.driver_id,
  }));
  const dispatch = useDispatch();

  useEffect(() => {
    const query = {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      driverApplicationStatus,
      ...(homeCity ? { homeCity } : {}),
    };
    fetchDrivers(query)(dispatch);
  }, [driverApplicationStatus, homeCity, page, dispatch]);

  const onRowClick = ({ row }) => {
    history.push(`/drivers-approval/funnel/${row.driver_id}`);
  };

  const onCitySelected = (value) => {
    const params = new URLSearchParams();
    params.append('page', 1);
    value && params.append('homeCity', value.id);
    history.push({ search: params.toString() });
  };

  const onPageChange = (p) => {
    const params = new URLSearchParams();
    params.append('page', p + 1);
    homeCity && params.append('homeCity', homeCity);
    history.push({ search: params.toString() });
  };

  if (!hasChangeDriverStatusPermission) {
    return <>Missing change_driver_status permission.</>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={cardHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em', flex: 1 }}>
            Driver Triage
          </Typography>
          <FilterSelect
            icon={<StationIcon />}
            label="City"
            placeholder="Filter by city"
            clearLabel="All cities"
            value={cities.find((c) => c.id === homeCity) || null}
            options={cities}
            getOptionLabel={(c) => `${c.cityName}, ${c.stateAbbr}`}
            onChange={onCitySelected}
          />
        </Stack>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <DataGrid
            rows={rows}
            columns={gridColumns}
            onRowClick={onRowClick}
            pageSize={PAGE_SIZE}
            loading={loading}
            paginationMode="server"
            rowCount={count}
            page={page - 1}
            onPageChange={onPageChange}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DriversTriage;
